import React, { useRef, useState, useEffect } from "react";
import { useVideoState } from "../../hooks/useVideoState";
import { VideoControls } from "./VideoControls";
import { VideoOverlay } from "./VideoOverlay";

interface CustomVideoPlayerProps {
  src: string;
  poster?: string;
  /** İzleme ölçümü: tarayıcının oynatılan aralıklarından İZLENEN saniye (ileri sarılan kısım sayılmaz) ve video süresi.
   *  final: duraklatıldı ya da bitti — hemen bildirilmeli. */
  onWatchProgress?: (watchedSeconds: number, durationSeconds: number, final?: boolean) => void;
}

const playedSeconds = (video: HTMLVideoElement) => {
  let total = 0;
  for (let i = 0; i < video.played.length; i += 1) total += video.played.end(i) - video.played.start(i);
  return total;
};

export function CustomVideoPlayer({ src, poster, onWatchProgress }: CustomVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef<number>();

  const {
    isPlaying,
    currentTime,
    duration,
    volume,
    speed,
    isFullscreen,
    togglePlay,
    handleTimeUpdate,
    handleSeek,
    handleVolumeChange,
    handleSpeedChange,
    toggleFullscreen,
    seekBackward,
    seekForward,
    cleanup,
  } = useVideoState(videoRef);

  useEffect(() => {
    return cleanup;
  }, [src, cleanup]);

  const handleMouseMove = () => {
    setShowControls(true);

    // Clear any existing timeout
    if (controlsTimeoutRef.current) {
      window.clearTimeout(controlsTimeoutRef.current);
    }

    // Only set a new timeout if the video is playing
    if (isPlaying) {
      controlsTimeoutRef.current = window.setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        window.clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  // Reset controls visibility when play state changes
  useEffect(() => {
    if (!isPlaying) {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        window.clearTimeout(controlsTimeoutRef.current);
      }
    }
  }, [isPlaying]);

  return (
    <div
      className="relative group w-full aspect-video bg-black rounded-lg overflow-hidden"
      style={{ cursor: isPlaying && !showControls ? "none" : "auto" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => {
        if (isPlaying) {
          setShowControls(false);
        }
      }}>
      <video
        ref={videoRef}
        className="w-full h-full"
        poster={poster}
        onTimeUpdate={() => {
          handleTimeUpdate();
          const v = videoRef.current;
          if (v && onWatchProgress && v.duration) onWatchProgress(playedSeconds(v), v.duration);
        }}
        onPause={() => {
          const v = videoRef.current;
          if (v && onWatchProgress && v.duration) onWatchProgress(playedSeconds(v), v.duration, true);
        }}
        onEnded={() => {
          const v = videoRef.current;
          if (v && onWatchProgress && v.duration) onWatchProgress(playedSeconds(v), v.duration, true);
        }}
        onLoadedMetadata={handleTimeUpdate}>
        <source src={src} type="video/mp4" />
        Tarayıcınız video etiketini desteklemiyor.
      </video>

      <VideoOverlay isVisible={!isPlaying || showControls} isPlaying={isPlaying} onPlayPause={togglePlay} />

      <VideoControls
        isVisible={!isPlaying || showControls}
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        volume={volume}
        speed={speed}
        isFullscreen={isFullscreen}
        onPlayPause={togglePlay}
        onSeek={handleSeek}
        onVolumeChange={handleVolumeChange}
        onSpeedChange={handleSpeedChange}
        onToggleFullscreen={toggleFullscreen}
        onSeekBackward={seekBackward}
        onSeekForward={seekForward}
      />
    </div>
  );
}
